// ============================================================
// TMR Portal Admin — cadastro/correção de uma pessoa do diretório de Partes (10 B5b)
//
// It lives in `components/` rather than inside `features/parties/` because it has two real consumers: the
// Partes screen and the Empresa cadastre's contact blocks, which is exactly the second-consumer rule
// (skill `frontend-architecture`, Regra 2).
//
// Unlike `UserFormModal`, it owns its mutations instead of reporting values to the caller. Two reasons:
// both callers would otherwise duplicate the same create/update wiring, and the cadastre needs "register
// this person and point the block at them" to be a single gesture — the split version is what produced a
// directory full of name-only people and duplicates (09 §10, F10).
// ============================================================
import { useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from './primitives';
import { TextField } from './fields';
import { PhoneField } from './PhoneField';
import { ErrorState, Skeleton } from './states';
import { Icons, StrokeIcon } from '../lib/icons';
import { maskCPF } from '../lib/format';
import { useCreateParty, useParty, useUpdateParty } from '../data/parties/queries';
import {
  emptyPartyForm,
  isPartyFormValid,
  toCreateRequest,
  toFormValues,
  toUpdateRequest,
  validateParty,
} from '../data/parties/form';
import type { PartyFormValues } from '../data/parties/form';
import type { PartyOption, PartyRecord } from '../data/parties/contract';
import { OverlayPortal } from './OverlayPortal';

/** Props for {@link PartyFormModal}. */
export interface PartyFormModalProps {
  /**
   * The person to correct (`PUT /parties/{id}`). Omit to register a new one.
   *
   * The record is loaded here rather than taken from the caller's list row: the row carries neither the
   * note nor the associations, and the endpoint replaces the whole record — saving from a row would
   * archive the note and unlink every Empresa.
   */
  partyId?: string;
  /** What the caller already has on screen, prefilled into a registration. Ignored when editing. */
  initial?: PartyFormValues;
  /** Line under the heading, when the caller wants to explain why the form opened. */
  hint?: ReactNode;
  onClose: () => void;
  /**
   * The person as they were saved, so the caller can point at them straight away.
   *
   * It is the same shape the pickers hand around ({@link PartyOption}) precisely so the Empresa cadastre
   * can feed it to its own `onPick` — registering somebody and selecting them are one action there.
   */
  onSaved?: (party: PartyOption) => void;
}

/**
 * Registers or corrects a person in the Parties directory.
 *
 * Only the name is required (`Party.ValidateDetails`): the legacy keeps these people as loose,
 * mostly-empty rows, and demanding a CPF would reject records that are perfectly usable. What *is* filled
 * in is validated, here and again on the server.
 *
 * Gated by `parties.manage` on the server — the caller renders it only for an operator who holds it,
 * since a form that can only 403 is worse than no form (skill `frontend-architecture`, Regra 5).
 */
export function PartyFormModal({ partyId, initial, hint, onClose, onSaved }: PartyFormModalProps) {
  const editing = Boolean(partyId);
  const record = useParty(partyId ?? null);

  // The registration form starts from the caller's values; the correction form waits for the record.
  if (editing) {
    if (record.isPending) return <Shell title="Carregando pessoa…" onClose={onClose}><Skeleton rows={4} /></Shell>;
    if (record.isError) {
      return (
        <Shell title="Pessoa não carregada" onClose={onClose}>
          <ErrorState error={record.error} onRetry={() => void record.refetch()} />
        </Shell>
      );
    }
    return <PartyForm record={record.data} hint={hint} onClose={onClose} onSaved={onSaved} />;
  }

  return <PartyForm initial={initial} hint={hint} onClose={onClose} onSaved={onSaved} />;
}

/** The form itself, mounted only once there is something complete to edit. */
function PartyForm({
  record,
  initial,
  hint,
  onClose,
  onSaved,
}: {
  record?: PartyRecord;
  initial?: PartyFormValues;
  hint?: ReactNode;
  onClose: () => void;
  onSaved?: (party: PartyOption) => void;
}) {
  const [values, setValues] = useState<PartyFormValues>(
    () => (record ? toFormValues(record) : initial ?? emptyPartyForm()),
  );
  const [touched, setTouched] = useState(false);

  const createParty = useCreateParty();
  const updateParty = useUpdateParty();
  const saving = createParty.isPending || updateParty.isPending;
  const failure = createParty.error ?? updateParty.error;

  const errors = validateParty(values);
  const set = <K extends keyof PartyFormValues>(key: K, value: PartyFormValues[K]) =>
    setValues((previous) => ({ ...previous, [key]: value }));

  const submit = () => {
    if (!isPartyFormValid(errors)) {
      setTouched(true);
      return;
    }

    if (record) {
      const request = toUpdateRequest(record, values);
      updateParty.mutate(
        { id: record.id, ...request },
        {
          onSuccess: () => {
            onSaved?.({ id: record.id, name: request.name, cpf: request.cpf, email: request.email, phone: request.phone });
            onClose();
          },
        },
      );
      return;
    }

    const request = toCreateRequest(values);
    createParty.mutate(request, {
      onSuccess: (result) => {
        onSaved?.({ id: result.id, name: request.name, cpf: request.cpf, email: request.email, phone: request.phone });
        onClose();
      },
    });
  };

  return (
    <Shell
      title={record ? record.name : 'Cadastrar pessoa'}
      eyebrow={record ? 'Editar pessoa' : 'Nova pessoa'}
      onClose={onClose}
      footer={
        <>
          <Btn variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Btn>
          <Btn variant="primary" icon={Icons.check} onClick={submit} disabled={saving}>
            {saving ? 'Salvando…' : record ? 'Salvar alterações' : 'Cadastrar pessoa'}
          </Btn>
        </>
      }
    >
      {hint && <div style={hintStyle}>{hint}</div>}

      <TextField
        label="Nome completo"
        required
        value={values.name}
        onChange={(v) => set('name', v)}
        error={touched && errors.name}
        placeholder="Ex.: Marina Costa Ferreira"
      />
      <div className="pa-grid-2">
        <TextField
          label="CPF"
          value={values.cpf}
          onChange={(v) => set('cpf', maskCPF(v))}
          error={touched && errors.cpf}
          placeholder="000.000.000-00 (opcional)"
          mono
        />
        <PhoneField value={values.phone} onChange={(v) => set('phone', v)} error={touched && errors.phone} />
      </div>
      <TextField
        label="E-mail"
        value={values.email}
        onChange={(v) => set('email', v)}
        error={touched && errors.email}
        placeholder="nome@empresa.com.br (opcional)"
      />

      <div>
        <label className="pa-form-label" htmlFor="party-note">Observação</label>
        <textarea
          id="party-note"
          value={values.note}
          onChange={(e) => set('note', e.target.value)}
          rows={3}
          placeholder="Anotação interna sobre a pessoa (opcional)"
          className="pa-input"
          style={{
            width: '100%',
            padding: '9px 12px',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--cream-50)',
            color: 'var(--ink-900)',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {failure && (
        <div role="alert" style={{ padding: '9px 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-100)', fontSize: 12.5, color: 'var(--ink-900)' }}>
          {messageOf(failure)}
        </div>
      )}

      <div style={hintStyle}>
        <StrokeIcon d={Icons.users} size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--stone-400)' }} />
        <span>
          Só o nome é obrigatório — CPF e e-mail são conferidos quando preenchidos. Esta pessoa passa a
          aparecer nas buscas do cadastro de Empresa.
        </span>
      </div>
    </Shell>
  );
}

/** The modal frame, shared by the form and by the states that stand in for it while it loads. */
function Shell({
  title,
  eyebrow = 'Cadastro de pessoas',
  children,
  footer,
  onClose,
}: {
  title: string;
  eyebrow?: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}) {
  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 520, maxWidth: '100%', maxHeight: '90vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-default)' }}>
            <div className="pa-eyebrow" style={{ marginBottom: 4 }}>{eyebrow}</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, letterSpacing: '-0.01em' }}>{title}</h3>
          </div>
          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>{children}</div>
          {footer && (
            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </OverlayPortal>
  );
}

const hintStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '9px 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--cream-100)',
  fontSize: 12.5,
  color: 'var(--fg-muted)',
  lineHeight: 1.5,
} as const;

/** An ApiError already carries operator-facing PT-BR detail — a duplicate CPF, typically (409). */
function messageOf(error: unknown): string {
  if (error instanceof ApiError && error.detail.trim()) return error.detail;
  return 'Não foi possível salvar. Tente novamente.';
}
